import { useParams } from "react-router";
import { GitMerge, LayoutTemplate, BarChart2, Lock } from "lucide-react";
import { Badge } from "../../../components/shared/Badge";
import { EnterprisePageShell } from "./EnterprisePageShell";
import { usePlatformEnterpriseModules, useTogglePlatformEnterpriseModule } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";

interface EntModule {
  moduleId: string; moduleKey: string; moduleName: string;
  description: string | null; isAvailable: boolean; isEnabled: boolean;
}

const MODULE_META: Record<string, { icon: React.ComponentType<{ className?: string }>; iconBg: string }> = {
  migrate: { icon: GitMerge, iconBg: "bg-indigo-600" },
  design: { icon: LayoutTemplate, iconBg: "bg-cyan-600" },
  analytics: { icon: BarChart2, iconBg: "bg-gray-400" },
};

export function PlatformEnterpriseAccessPage() {
  const { id: enterpriseId } = useParams<{ id: string }>();
  const { data: modules = [], isLoading } = usePlatformEnterpriseModules(enterpriseId ?? null);
  const toggle = useTogglePlatformEnterpriseModule();
  const { toast } = useToast();

  const typedModules = modules as EntModule[];

  const handleToggle = async (mod: EntModule) => {
    if (!enterpriseId) return;
    try {
      await toggle.mutateAsync({ id: enterpriseId, moduleId: mod.moduleId, enabled: !mod.isEnabled });
      toast({ title: `${mod.moduleName} ${!mod.isEnabled ? "enabled" : "disabled"}`, variant: "success" });
    } catch {
      toast({ title: "Failed to update module access", variant: "error" });
    }
  };

  return (
    <EnterprisePageShell enterpriseId={enterpriseId ?? ""} pageName="Access level">
      {isLoading ? (
        <div className="p-8 text-center text-sm text-ds-text-muted">Loading modules…</div>
      ) : (
        <div className="space-y-3">
          {typedModules.map((mod) => {
            const meta = MODULE_META[mod.moduleKey] ?? { icon: BarChart2, iconBg: "bg-gray-400" };
            const Icon = meta.icon;
            return (
              <div key={mod.moduleId}
                className={`bg-white border rounded-card shadow-card p-5 flex items-center gap-4 transition-colors ${mod.isEnabled ? "border-indigo-200" : "border-ds-border"}`}>
                <div className={`w-10 h-10 rounded-xl ${meta.iconBg} flex items-center justify-center flex-shrink-0`}>
                  {!mod.isAvailable
                    ? <Lock className="w-4 h-4 text-white" />
                    : <Icon className="w-5 h-5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-ds-text">{mod.moduleName}</p>
                    {!mod.isAvailable && (
                      <Badge variant="gray"><Lock className="w-2.5 h-2.5 mr-1" />Not available</Badge>
                    )}
                  </div>
                  <p className="text-xs text-ds-text-muted mt-0.5">{mod.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Badge variant={mod.isEnabled ? "green" : "gray"}>
                    {mod.isEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                  <button
                    onClick={() => handleToggle(mod)}
                    disabled={toggle.isPending}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${mod.isEnabled ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}>
                    {mod.isEnabled ? "Disable" : "Enable"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </EnterprisePageShell>
  );
}
